/**
 * AICoachingCard Component
 * Dynamic coaching topics based on user context
 * Replaces the old CoachingCheckIn with rule-based + AI-generated topics
 */

import React, { useState, useEffect } from 'react';
import Card from '../common/Card';
import { FaStar } from 'react-icons/fa';
import dashboardService from '../../services/dashboardService';

const AICoachingCard = ({ profile, onTopicClick }) => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopics = async () => {
      setLoading(true);
      try {
        const fetchedTopics = await dashboardService.fetchCoachingTopics(profile);
        setTopics(fetchedTopics);
      } catch (error) {
        console.error('Error fetching coaching topics:', error);
        // Fallback topics will be loaded by service
      } finally {
        setLoading(false);
      }
    };

    if (profile) {
      fetchTopics();
    }
  }, [profile]);

  return (
    <Card variant="white" padding="lg" className="mb-6">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <FaStar className="w-5 h-5 text-primary-600" />
          <h2 className="font-display text-xl font-semibold text-text-primary">
            Hva trenger du hjelp med nå?
          </h2>
        </div>

        <p className="text-sm text-text-secondary">
          Start en samtale om et av disse temaene
        </p>

        {/* Topics grid */}
        {loading ? (
          // Loading skeleton
          <div className="grid grid-cols-1 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-20 skeleton-shimmer rounded-lg"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {topics.map((topic, index) => (
              <button
                key={topic.id}
                onClick={() => onTopicClick(topic)}
                className="text-left p-4 rounded-lg border-2 border-border hover:border-primary-400 hover:bg-primary-50 transition-all duration-200 ease-smooth group animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <p className="font-medium text-text-primary mb-1 group-hover:text-primary-800">
                  {topic.label}
                </p>
                <p className="text-xs text-text-muted group-hover:text-primary-700">
                  {topic.description}
                </p>
              </button>
            ))}
          </div>
        )}

        {/* Fallback message if no topics */}
        {!loading && topics.length === 0 && (
          <div className="text-center py-6">
            <p className="text-sm text-text-muted">
              Kunne ikke laste veiledningstemaer. Prøv igjen senere.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AICoachingCard;
